import { create } from 'zustand';
import { axiosWithAuth } from '../utils/axiosWithAuth';
import type { FriendStatus, FriendsOnlineSnapshotPayload } from '../utils/signalr/hubEvents';

export interface Friend {
  id: number;
  friendID: string;
  name: string;
  avatarUrl?: string;
  status: FriendStatus;
  lastOnlineTime?: string | null;
}

export interface FriendsListResponse {
  incoming: Friend[];
  outgoing: Friend[];
  friends: Friend[];
}

interface FriendState {
  incoming: Friend[];
  outgoing: Friend[];
  friends: Friend[];
  isHydrated: boolean;
  isLoading: boolean;
  loadError: string | null;

  hydrateFromRest: (data: FriendsListResponse) => void;
  refreshFromRest: () => Promise<boolean>;
  updateFriendStatus: (userId: number, status: FriendStatus) => void;
  applyOnlineSnapshot: (snapshot: FriendsOnlineSnapshotPayload) => void;
  reset: () => void;
}

const emptyLists = {
  incoming: [] as Friend[],
  outgoing: [] as Friend[],
  friends: [] as Friend[],
  isHydrated: false,
  isLoading: false,
  loadError: null as string | null,
};

function normalizeFriend(raw: Friend & { status?: number }): Friend {
  return {
    ...raw,
    status: (raw.status ?? 0) as FriendStatus,
  };
}

function normalizeFriendsList(data: FriendsListResponse): FriendsListResponse {
  return {
    incoming: (data.incoming ?? []).map(normalizeFriend),
    outgoing: (data.outgoing ?? []).map(normalizeFriend),
    friends: (data.friends ?? []).map(normalizeFriend),
  };
}

function patchFriendStatus(friends: Friend[], userId: number, status: FriendStatus): Friend[] {
  const targetId = Number(userId);
  return friends.map((f) => {
    if (Number(f.id) !== targetId) return f;
    return {
      ...f,
      status,
      ...(status === 1 ? { lastOnlineTime: null } : {}),
    };
  });
}

export const useFriendStore = create<FriendState>((set, get) => ({
  ...emptyLists,

  hydrateFromRest: (data) =>
    set({
      ...normalizeFriendsList(data),
      isHydrated: true,
      loadError: null,
    }),

  refreshFromRest: async () => {
    set({ isLoading: true, loadError: null });
    try {
      const res = await axiosWithAuth('post', '/api/User/friends/list', {}, { logErrors: false });
      if (!res?.Success || !res.Data) {
        const message = typeof res?.message === 'string' ? res.message : '????????';
        set({ isLoading: false, loadError: message });
        return false;
      }
      get().hydrateFromRest(res.Data as FriendsListResponse);
      set({ isLoading: false });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : '????????';
      set({ isLoading: false, loadError: message });
      return false;
    }
  },

  updateFriendStatus: (userId, status) =>
    set((s) => ({
      friends: patchFriendStatus(s.friends, userId, status),
      incoming: patchFriendStatus(s.incoming, userId, status),
      outgoing: patchFriendStatus(s.outgoing, userId, status),
    })),

  applyOnlineSnapshot: ({ onlineUserIds }) => {
    const onlineSet = new Set(onlineUserIds.map((id) => Number(id)));
    set((s) => ({
      friends: s.friends.map((f) => {
        const isOnline = onlineSet.has(Number(f.id));
        return {
          ...f,
          status: isOnline ? (1 as FriendStatus) : (0 as FriendStatus),
          ...(isOnline ? { lastOnlineTime: null } : {}),
        };
      }),
    }));
  },

  reset: () => set(emptyLists),
}));
